import { createEffect, createEvent, createStore, sample } from 'effector';
import { recordFillFx, type Position } from '../../entities/position';
import { toastShown } from '../../entities/toast';
import { api, ApiError, unwrap, type Schemas } from '../../shared/api/client';
import { i18n } from '../../shared/lib/i18n';
import { latest } from '../../shared/lib/latest';

export interface FillRequest {
  position: Position;
  level: number;
}

export const fillDialogOpened = createEvent<FillRequest>();
export const fillDialogClosed = createEvent();

export const $request = createStore<FillRequest | null>(null)
  .on(fillDialogOpened, (_, request) => request)
  .reset(fillDialogClosed, recordFillFx.done);

export const previewFillFx = createEffect(
  ({ id, body }: { id: number; body: Schemas['FillPreviewIn'] }) =>
    unwrap(
      api.POST('/api/v1/positions/{position_id}/preview-fill', {
        params: { path: { position_id: id } },
        body,
      }),
    ),
);

/** Call this, not previewFillFx: only the latest input's answer is shown. */
export const fillPreview = latest(previewFillFx, { reset: fillDialogOpened });

export const $preview = createStore<Schemas['HoldingOut'] | null>(null)
  .on(fillPreview.done, (_, holding) => holding)
  .reset(fillDialogOpened);

export const $errors = createStore<Record<string, string>>({})
  .on(recordFillFx.failData, (_, error) => (error instanceof ApiError ? error.fields : {}))
  .reset(fillDialogOpened, recordFillFx.done);

sample({
  clock: recordFillFx.done,
  fn: ({ params }) => ({
    message: i18n.t('recordFill.recorded', { n: params.body.level_n }),
    tone: 'neutral' as const,
  }),
  target: toastShown,
});

sample({
  clock: recordFillFx.failData,
  filter: (error) => !(error instanceof ApiError) || Object.keys(error.fields).length === 0,
  fn: (error) => ({ message: error.message, tone: 'error' as const }),
  target: toastShown,
});
